import { computed, ref } from "vue";
import { useStore } from "vuex";
import { AuthenticationContext } from "@/auth/msal-context";
import { MEMBERS_NAMESPACE } from "@/store/namespace-types";
import { GET_MEMBERCOUNT, GET_MEMBERGROUPS } from "@/store/action-types";
import { MEMBER_ADMIN_ROLES, exitsInYear, pendingMemberGroups } from "@/composables/memberStatus";

/**
 * Member groups and member count from the store, shared by the start page and the
 * Mitglieder pages. Callers decide whether to load the member groups (see canManageMembers).
 */
export function useMemberGroups() {
  const store = useStore();
  const canManageMembers = AuthenticationContext.checkRoles(MEMBER_ADMIN_ROLES);
  const loading = ref(false);
  const loadError = ref(false);

  const memberCount = computed(() => store.state[MEMBERS_NAMESPACE].count);
  const memberGroups = computed(() => Array.from(store.state[MEMBERS_NAMESPACE].membergrouplist?.values() ?? []));
  const pendingGroups = computed(() => pendingMemberGroups(memberGroups.value));
  const currentYear = new Date().getFullYear();
  const exitsThisYear = computed(() => exitsInYear(memberGroups.value, currentYear));

  const loadMemberCount = () => store.dispatch(`${MEMBERS_NAMESPACE}/${GET_MEMBERCOUNT}`);

  const loadMemberGroups = async () => {
    loading.value = true;
    loadError.value = false;
    try {
      await store.dispatch(`${MEMBERS_NAMESPACE}/${GET_MEMBERGROUPS}`);
    } catch (error) {
      loadError.value = true;
    } finally {
      loading.value = false;
    }
  };

  return {
    canManageMembers,
    loading,
    loadError,
    memberCount,
    memberGroups,
    pendingGroups,
    currentYear,
    exitsThisYear,
    loadMemberCount,
    loadMemberGroups,
  };
}
